import { EApplications, EChannels } from 'core/model/enums';
import type {
  IMaintenanceSecondaryValues,
  TCreateMaintenanceParams,
  TMaintenance,
  TMaintenanceApplicationKey,
  TMaintenanceList,
  TMaintenanceListItem,
  TUpdateMaintenanceParams,
} from 'core/model/interfaces/maintenance';
import type {
  IMaintenanceBaseApplicationChannelDTO,
  TCreateMaintenanceDTO,
  TMaintenanceDTO,
  TMaintenanceListItemDTO,
  TUpdateMaintenanceDTO,
} from './maintenance.dto';
import { getMaintenanceStatusValues, toMaintenanceStatusValues } from './maintenance.utils';

export const mapMaintenanceApplicationChannels = (
  applicationChannels: TMaintenanceDTO['channels'],
): Array<TMaintenanceApplicationKey> => {
  const list = applicationChannels.flatMap(
    applicationChannel =>
      `${applicationChannel.applicationId}-${applicationChannel.channelId}` as TMaintenanceApplicationKey,
  );

  return list;
};

export const toApplicationChannels = (maintenance: TUpdateMaintenanceParams) => {
  const applicationChannels: Array<IMaintenanceBaseApplicationChannelDTO> = [];

  maintenance.applicationChannels.forEach(channelString => {
    const [application, channel] = channelString.split('-') as [`${EApplications}`, `${EChannels}`];

    const applicationId = Number(application);

    const channelId = Number(channel);

    applicationChannels.push({ applicationId, channelId });
  });

  return applicationChannels;
};

export const toBranchList = (branches: IMaintenanceSecondaryValues['branches']) => {
  const list = branches.map(branch => {
    const [branchId, distributorId] = branch.split('-');

    return { branchId, distributorId };
  });

  return list;
};

export const toMaintenanceListItem = (maintenanceDTO: TMaintenanceListItemDTO): TMaintenanceListItem => {
  const maintenanceStatusValues = getMaintenanceStatusValues(maintenanceDTO);

  const maintenance: TMaintenanceList[number] = {
    id: maintenanceDTO.id,
    title: maintenanceDTO.name,
    description: maintenanceDTO.description,
    ...maintenanceStatusValues,
  };

  return maintenance;
};

export const toGetMaintenanceList = (maintenanceListDTO: Array<TMaintenanceListItemDTO>): TMaintenanceList =>
  maintenanceListDTO.map(toMaintenanceListItem);

export const toMaintenanceById = (maintenanceDTO: TMaintenanceDTO, id: TMaintenanceListItem['id']): TMaintenance => {
  const baseValues = toMaintenanceListItem(maintenanceDTO);

  const channels = maintenanceDTO.channels.filter(channel => channel.checked);

  const applicationChannels = mapMaintenanceApplicationChannels(channels);

  const branches = maintenanceDTO.distributors.map(
    distributor => `${distributor.branchId}-${distributor.distributorId}`,
  );

  const scheduledValues = getMaintenanceStatusValues(maintenanceDTO);

  const maintenance: TMaintenance = {
    ...baseValues,
    ...scheduledValues,
    applicationChannels,
    branches,
    id,
  };

  return maintenance;
};

export const toCreateMaintenance = (maintenance: TCreateMaintenanceParams): TCreateMaintenanceDTO => {
  const maintenanceDTO: TCreateMaintenanceDTO = {
    name: maintenance.title,
    description: maintenance.description,
  };

  return maintenanceDTO;
};

export const toUpdateMaintenance = (maintenance: TUpdateMaintenanceParams): TUpdateMaintenanceDTO => {
  const baseValues = toCreateMaintenance(maintenance);

  const scheduledValues = toMaintenanceStatusValues(maintenance);

  const applicationChannels = toApplicationChannels(maintenance);

  const distributors = toBranchList(maintenance.branches);

  const maintenanceDTO: TUpdateMaintenanceDTO = {
    ...baseValues,
    channels: applicationChannels,
    distributors,
    ...scheduledValues,
  };

  return maintenanceDTO;
};
